import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CustomerPicker, initials } from '../components/Customers.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * SMS and WhatsApp: the business's own Twilio account sends texts and WhatsApp messages to the
 * customers who agreed to them. The inbox keeps every conversation, campaigns go to an audience
 * from Marketing, and STOP is honoured on its own. Nothing goes out until the account is set up.
 */

type Tab = 'inbox' | 'campaigns' | 'templates' | 'contacts' | 'settings';
type Channel = 'sms' | 'whatsapp';
type CState = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled';
type MState = 'queued' | 'sending' | 'sent' | 'delivered' | 'read' | 'undelivered' | 'failed' | 'skipped' | 'received';
type Variable = 'first_name' | 'name' | 'business';
const VARIABLES: Variable[] = ['first_name', 'name', 'business'];

interface Overview {
  ready: Record<Channel, boolean>;
  quiet: { from: number | null; to: number | null; now: boolean } | null;
  last_error: string | null;
  month: { sent: number; delivered: number; failed: number; received: number; segments: number; opted_out: number };
  unread: { unread: number; threads: number };
  reachable: Record<Channel, number>;
  recent: CampaignRow[];
}
interface CampaignRow {
  id: string;
  number: string;
  name: string;
  channel: Channel;
  state: CState;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  recipients: number;
  sent: number;
  delivered: number;
  failed: number;
  replies: number;
  opted_out: number;
  audience?: string | null;
}
interface Campaign extends CampaignRow {
  body: string;
  template: string | null;
  audience: string | null;
  add_stop: boolean;
  last_error: string | null;
  tested_at: string | null;
  sent_by_name: string | null;
  audience_name: string | null;
  audience_count: { total: number; reachable: number } | null;
  template_name: string | null;
  preview: string;
  length: Length;
  delivered_by_hour: { at: string; n: number }[];
  ready: boolean;
}
interface Template {
  id: string;
  name: string;
  channel: Channel;
  purpose: 'marketing' | 'service';
  body: string;
  content_sid: string | null;
  variables: Variable[];
  active?: boolean;
  used?: number;
}
interface Setup {
  ready: Record<Channel, boolean>;
  business: string;
  stop_note: string;
  audiences: { id: string; name: string; sms: number; whatsapp: number }[];
  templates: Template[];
}
interface ThreadRow {
  id: string;
  phone: string;
  channel: Channel;
  customer: string | null;
  customer_name: string | null;
  profile_name: string | null;
  last_at: string;
  last_text: string | null;
  last_out: boolean;
  unread: number;
  archived: boolean;
  window_open: boolean;
}
interface Message {
  id: string;
  incoming: boolean;
  body: string | null;
  state: MState;
  error: string | null;
  segments: number | null;
  media: number | null;
  at: string;
  delivered_at: string | null;
  read_at: string | null;
  campaign: string | null;
  campaign_number: string | null;
  campaign_name: string | null;
  by_name: string | null;
}
interface Thread {
  id: string;
  phone: string;
  channel: Channel;
  profile_name: string | null;
  archived: boolean;
  customer: { id: string; name: string; phone: string | null; email: string | null } | null;
  agreed_at: string | null;
  stopped: boolean;
  window_open: boolean;
  window_until: string | null;
  messages: Message[];
}
interface Contact {
  id: string;
  name: string;
  phone: string | null;
  reachable: boolean;
  agreed_at: string | null;
  opted_out_at: string | null;
  received: number;
}
interface Settings {
  account_sid: string | null;
  sms_from: string | null;
  service_sid: string | null;
  whatsapp_from: string | null;
  site_url: string | null;
  business_name: string | null;
  stop_note: string;
  per_minute: number;
  per_day: number;
  quiet_from: number | null;
  quiet_to: number | null;
  verified_at: string | null;
  account_name: string | null;
  last_error: string | null;
  has_token: boolean;
  token_readable: boolean;
  can_keep_token: boolean;
  missing: Record<Channel, string[]>;
  inbound_url: string | null;
}

const TABS: { key: Tab; icon: IconName; perm?: string }[] = [
  { key: 'inbox', icon: 'inbox' },
  { key: 'campaigns', icon: 'send' },
  { key: 'templates', icon: 'file' },
  { key: 'contacts', icon: 'users' },
  { key: 'settings', icon: 'settings', perm: 'sms.settings' },
];
const STATE: Record<CState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  scheduled: { tone: 'info', icon: 'clock' },
  sending: { tone: 'warn', icon: 'send' },
  sent: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const CH_ICON: Record<Channel, IconName> = { sms: 'chat', whatsapp: 'phone' };
const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 1000) / 10}%` : '—');
const sample = (business: string) => ({ first_name: 'Ana', name: 'Ana Hoxha', business });

// ---- message length, the same rules as the server ----------------------------------------------

const GSM = new Set('@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà');
const GSM_EXT = new Set('^{}\\[~]|€');
interface Length {
  plain: boolean;
  units: number;
  segments: number;
  perPart: number;
  odd: string[];
}
function lengthOf(text: string): Length {
  let plain = true;
  let units = 0;
  const odd = new Set<string>();
  for (const ch of text) {
    if (GSM.has(ch)) units += 1;
    else if (GSM_EXT.has(ch)) units += 2;
    else {
      plain = false;
      odd.add(ch);
    }
  }
  if (!plain) {
    units = 0;
    for (const ch of text) units += ch.length;
  }
  const one = plain ? 160 : 70;
  const part = plain ? 153 : 67;
  const segments = units === 0 ? 0 : units <= one ? 1 : Math.ceil(units / part);
  return { plain, units, segments, perPart: segments > 1 ? part : one, odd: [...odd].slice(0, 10) };
}
const plainLetters = (text: string) =>
  text.replace(/ë/g, 'e').replace(/Ë/g, 'E').replace(/ç/g, 'c').replace(/Ç/g, 'C').replace(/[‘’]/g, "'").replace(/[“”„]/g, '"').replace(/[–—]/g, '-').replace(/…/g, '...');
const fill = (body: string, v: Record<Variable, string | null>) =>
  body.replace(/\{(first_name|name|business)(?:\|([^}]{0,40}))?\}/g, (_, k: Variable, fallback?: string) => v[k] ?? fallback ?? '').replace(/[ \t]{2,}/g, ' ');
const templateText = (tpl: Pick<Template, 'body' | 'name' | 'variables'>, v: Record<Variable, string | null>) =>
  (tpl.body || tpl.name).replace(/\{\{(\d+)\}\}/g, (_, n: string) => {
    const k = tpl.variables[Number(n) - 1];
    return k ? (v[k] ?? '-') : `{{${n}}}`;
  });

/** "123 / 160 · 1 message", and why it is 70 when a letter outside the plain alphabet is used. */
function LengthLine({ text, onPlain }: { text: string; onPlain?: () => void }) {
  const l = lengthOf(text);
  const limit = l.segments > 1 ? l.segments * l.perPart : l.perPart;
  const fixable = l.odd.some((c) => /[ëËçÇ‘’“”„–—…]/.test(c));
  return (
    <span className={`sms-length${l.segments > 2 ? ' warn' : ''}`} aria-live="polite">
      <span className="tabular">
        {l.units} / {limit}
      </span>
      <span>{tn('sms.n_parts', Math.max(1, l.segments))}</span>
      {!l.plain ? (
        <span className="sms-odd" title={t('sms.odd_hint')}>
          {t('sms.odd_letters', { letters: l.odd.join(' ') })}
          {fixable && onPlain ? (
            <button type="button" className="link" onClick={onPlain}>
              {t('sms.make_plain')}
            </button>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

function CampaignBadge({ state }: { state: CState }) {
  const s = STATE[state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={t(`sms.st_${state}` as MessageKey)} />;
}
function ChannelTag({ channel }: { channel: Channel }) {
  return (
    <span className={`sms-ch sms-ch-${channel}`}>
      <Icon name={CH_ICON[channel]} size={12} />
      {t(`sms.ch_${channel}` as MessageKey)}
    </span>
  );
}

export function Sms() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tabs = TABS.filter((x) => !x.perm || can(x.perm));
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'inbox') as Tab;
  const { data: o, reload } = useAction<Overview>('sms.overview');
  const [version, setVersion] = useState(0);
  const [edit, setEdit] = useState<string | 'new' | null>(null);
  const [writing, setWriting] = useState(false);
  const open = params.get('c');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  useEffect(() => subscribe(['sms'], () => void reload()), [reload]);
  const manage = can('sms.manage');
  const ready = o ? o.ready.sms || o.ready.whatsapp : true;

  return (
    <Shell>
      <div className={`page reports sms-page tab-${tab}${tab === 'inbox' && params.get('t') ? ' has-thread' : ''}`}>
        <div className="page-head">
          <div>
            <h1>{t('sms.title')}</h1>
            <p className="muted">{t('sms.subtitle')}</p>
          </div>
          <div className="page-actions">
            {can('sms.reply') ? (
              <Button icon="edit" onClick={() => setWriting(true)}>
                {t('sms.new_message')}
              </Button>
            ) : null}
            {manage ? (
              <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
                {t('sms.new_campaign')}
              </Button>
            ) : null}
          </div>
        </div>
        {o && !ready ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>
              {t('sms.not_ready')}{' '}
              {can('sms.settings') ? (
                <button className="link" onClick={() => set({ tab: 'settings', t: null, c: null })}>
                  {t('sms.set_up')}
                </button>
              ) : (
                t('sms.ask_manager')
              )}
            </span>
          </p>
        ) : o?.last_error ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('sms.twilio_error', { error: o.last_error })}</span>
          </p>
        ) : null}
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('sms.at_a_glance')}>
            <Kpi label={t('sms.k_unread')} value={String(o.unread.unread)} hint={o.unread.threads ? tn('sms.k_unread_hint', o.unread.threads) : t('sms.k_all_read')} warn={o.unread.unread > 0} />
            <Kpi
              label={t('sms.k_sent')}
              value={String(o.month.sent)}
              hint={o.month.sent ? t('sms.k_delivered', { pct: pct(o.month.delivered, o.month.sent), n: o.month.segments }) : t('sms.k_nothing_sent')}
              warn={o.month.failed > 0 && o.month.failed / Math.max(1, o.month.sent) > 0.05}
            />
            <Kpi label={t('sms.k_received')} value={String(o.month.received)} hint={o.month.opted_out ? tn('sms.k_opted_out', o.month.opted_out) : t('sms.k_nobody_out')} />
            <Kpi label={t('sms.k_reach')} value={String(o.reachable.sms)} hint={t('sms.k_reach_hint', { n: o.reachable.whatsapp })} />
          </section>
        )}
        <div className="segmented tabs sms-tabs" role="tablist" aria-label={t('sms.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'inbox' ? null : x.key, f: null, t: null })}>
              <Icon name={x.icon} size={16} />
              {t(`sms.tab_${x.key}` as MessageKey)}
              {x.key === 'inbox' && o?.unread.unread ? <span className="count">{o.unread.unread}</span> : null}
            </button>
          ))}
        </div>
        {!o ? null : tab === 'inbox' ? (
          <InboxTab params={params} set={set} onChange={reload} />
        ) : tab === 'campaigns' ? (
          <CampaignsTab key={version} params={params} set={set} onOpen={(id) => set({ c: id })} onNew={manage ? () => setEdit('new') : undefined} />
        ) : tab === 'templates' ? (
          <TemplatesTab key={version} />
        ) : tab === 'contacts' ? (
          <ContactsTab key={version} params={params} set={set} onChange={changed} />
        ) : (
          <SettingsTab onChange={changed} />
        )}
      </div>
      {open ? <CampaignDialog key={`${open}-${version}`} id={open} onClose={() => set({ c: null })} onChange={changed} onEdit={(id) => setEdit(id)} onOpen={(id) => set({ c: id })} /> : null}
      {edit ? (
        <Editor
          id={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async (id) => {
            setEdit(null);
            await changed();
            set({ c: id, tab: 'campaigns' });
          }}
        />
      ) : null}
      {writing ? (
        <NewMessage
          onClose={() => setWriting(false)}
          onSent={async (thread) => {
            setWriting(false);
            await changed();
            set({ tab: null, t: thread, c: null });
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- inbox ----------------------------------------------------------------------------------------------

const VIEWS = ['open', 'unread', 'archived'] as const;
const CH_FILTERS = ['all', 'sms', 'whatsapp'] as const;

function InboxTab({ params, set, onChange }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onChange: () => Promise<unknown> }) {
  const [view, setView] = useState<(typeof VIEWS)[number]>('open');
  const [channel, setChannel] = useState<(typeof CH_FILTERS)[number]>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ThreadRow[] | null>(null);
  const [pulse, setPulse] = useState(0);
  const selected = params.get('t');
  const open = useRef(selected);
  open.current = selected;
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const load = useCallback(
    () =>
      action<ThreadRow[]>('sms.threads', { filter: view, channel, ...(q ? { search: q } : {}) }).then(
        (r) => setRows(r),
        () => setRows([]),
      ),
    [view, channel, q],
  );
  useEffect(() => void load(), [load]);
  useEffect(
    () =>
      subscribe(['sms'], (_e, data: { thread?: string; read?: boolean }) => {
        void load();
        if (!data?.read && (!data?.thread || data.thread === open.current)) setPulse((n) => n + 1);
      }),
    [load],
  );
  const select = (id: string | null) => set({ t: id });
  return (
    <div className={`lc-inbox sms-inbox${selected ? ' has-chat' : ''}`}>
      <aside className="lc-list panel" aria-label={t('sms.conversations')}>
        <div className="lc-list-tools">
          <div className="lc-search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('sms.search_inbox')} aria-label={t('sms.search_inbox')} />
          </div>
          <div className="lc-views" role="group" aria-label={t('sms.show')}>
            {VIEWS.map((v) => (
              <button key={v} type="button" aria-pressed={view === v} className={`chip${view === v ? ' active' : ''}`} onClick={() => setView(v)}>
                {t(`sms.v_${v}` as MessageKey)}
              </button>
            ))}
          </div>
          <div className="segmented subtle sms-chf" role="radiogroup" aria-label={t('sms.channel')}>
            {CH_FILTERS.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={channel === c} className={channel === c ? 'active' : ''} onClick={() => setChannel(c)}>
                {t(`sms.chf_${c}` as MessageKey)}
              </button>
            ))}
          </div>
        </div>
        {!rows ? (
          <div className="lc-rows">
            <Skeleton h={300} />
          </div>
        ) : !rows.length ? (
          <EmptyState icon="inbox" title={q ? t('sms.none_found') : t(`sms.empty_${view}` as MessageKey)} body={q || view !== 'open' ? undefined : t('sms.empty_body')} />
        ) : (
          <ul className="lc-rows">
            {rows.map((r) => {
              const who = r.customer_name ?? r.profile_name ?? r.phone;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    className={`lc-row${selected === r.id ? ' active' : ''}${r.unread ? ' unread' : ''}`}
                    aria-current={selected === r.id || undefined}
                    onClick={() => select(r.id)}
                  >
                    <span className={`lc-avatar sms-avatar-${r.channel}`} aria-hidden="true">
                      {r.customer_name || r.profile_name ? initials(who) : <Icon name="phone" size={16} />}
                    </span>
                    <span className="lc-row-main">
                      <span className="lc-row-top">
                        <strong>{who}</strong>
                        <time dateTime={r.last_at}>{since(r.last_at)}</time>
                      </span>
                      <span className="lc-row-preview">
                        {r.last_out ? <span className="muted">{t('sms.you_prefix')} </span> : null}
                        {r.last_text}
                      </span>
                      <span className="lc-row-tags">
                        <ChannelTag channel={r.channel} />
                        {r.customer_name ? <span className="muted small tabular">{r.phone}</span> : !r.profile_name ? <span className="tag">{t('sms.unknown')}</span> : null}
                        {r.channel === 'whatsapp' && !r.window_open ? (
                          <span className="tag" title={t('sms.window_closed_hint')}>
                            <Icon name="lock" size={12} /> {t('sms.template_only')}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    {r.unread ? (
                      <span className="count lc-count" aria-label={tn('sms.n_unread', r.unread)}>
                        {r.unread}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>
      <section className="lc-thread-pane panel" aria-label={t('sms.conversation')}>
        {selected ? (
          <ThreadView
            key={selected}
            id={selected}
            pulse={pulse}
            onBack={() => select(null)}
            onChange={async () => {
              await load();
              await onChange();
            }}
          />
        ) : (
          <EmptyState icon="chat" title={t('sms.pick')} body={t('sms.pick_hint')} />
        )}
      </section>
    </div>
  );
}

const TICK: Partial<Record<MState, { icon: IconName; key: MessageKey }>> = {
  queued: { icon: 'clock', key: 'sms.m_queued' },
  sending: { icon: 'clock', key: 'sms.m_queued' },
  sent: { icon: 'check', key: 'sms.m_sent' },
  delivered: { icon: 'checkCircle', key: 'sms.m_delivered' },
  read: { icon: 'eye', key: 'sms.m_read' },
  undelivered: { icon: 'alert', key: 'sms.m_undelivered' },
  failed: { icon: 'alert', key: 'sms.m_failed' },
  skipped: { icon: 'minus', key: 'sms.m_skipped' },
};
const dayOf = (iso: string) => new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso));
const timeOf = (iso: string) => new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

function ThreadView({ id, pulse, onBack, onChange }: { id: string; pulse: number; onBack: () => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { fail, toast } = useUi();
  const [th, setTh] = useState<Thread | null>(null);
  const [linking, setLinking] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const load = useCallback(() => action<Thread>('sms.thread', { id }).then(setTh, fail), [id, fail]);
  useEffect(() => void load(), [load, pulse]);
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [th?.messages.length]);
  if (!th) return <Skeleton h={420} />;
  const who = th.customer?.name ?? th.profile_name ?? th.phone;
  const reply = can('sms.reply');
  const archive = async () => {
    try {
      await action('sms.archive', { id: th.id, archived: !th.archived });
      toast(th.archived ? t('sms.unarchived') : t('sms.archived'));
      await onChange();
      if (!th.archived) onBack();
      else await load();
    } catch (err) {
      fail(err);
    }
  };
  let lastDay = '';
  return (
    <div className="lc-thread">
      <header className="lc-thread-head">
        <IconButton icon="back" label={t('sms.back')} onClick={onBack} className="lc-back" />
        <span className={`lc-avatar lg sms-avatar-${th.channel}`} aria-hidden="true">
          {th.customer || th.profile_name ? initials(who) : <Icon name="phone" size={18} />}
        </span>
        <div className="lc-thread-who">
          <h2>{who}</h2>
          <p className="muted">
            <span className="tabular">{th.phone}</span> · {t(`sms.ch_${th.channel}` as MessageKey)}
          </p>
        </div>
        <div className="lc-thread-actions">
          {reply ? (
            <Button size="sm" variant="ghost" icon="contact" onClick={() => setLinking(true)}>
              {th.customer ? t('sms.change_customer') : t('sms.link_customer')}
            </Button>
          ) : null}
          {reply ? (
            <Button size="sm" variant="ghost" icon="archive" onClick={() => void archive()}>
              {th.archived ? t('sms.unarchive') : t('sms.archive')}
            </Button>
          ) : null}
        </div>
      </header>
      <div className="lc-thread-body">
        <div className="lc-msgs" ref={list} aria-live="polite">
          {th.messages.map((m) => {
            const d = dayOf(m.at);
            const showDay = d !== lastDay;
            lastDay = d;
            const tick = m.incoming ? null : TICK[m.state];
            const bad = m.state === 'failed' || m.state === 'undelivered';
            return (
              <div key={m.id} className="sms-msg-wrap">
                {showDay ? <span className="lc-day">{d}</span> : null}
                <div className={`lc-msg ${m.incoming ? 'visitor' : 'staff'}${bad ? ' sms-bad' : ''}`}>
                  {m.campaign_number ? (
                    <span className="lc-msg-who">
                      <Icon name="send" size={12} /> {m.campaign_number} · {m.campaign_name}
                    </span>
                  ) : !m.incoming && m.by_name ? (
                    <span className="lc-msg-who">{m.by_name}</span>
                  ) : null}
                  <p>{m.body}</p>
                  {m.media ? <span className="muted small">{tn('sms.n_media', m.media)}</span> : null}
                  <span className="sms-msg-foot">
                    <time dateTime={m.at}>{timeOf(m.at)}</time>
                    {tick ? (
                      <span className={`sms-tick${bad ? ' bad' : ''}`} title={m.error ?? undefined}>
                        <Icon name={tick.icon} size={12} />
                        {t(tick.key)}
                      </span>
                    ) : null}
                  </span>
                  {bad && m.error ? <span className="sms-err">{m.error}</span> : null}
                </div>
              </div>
            );
          })}
        </div>
        <aside className="lc-side">
          <dl className="lc-facts">
            <dt>{t('sms.f_phone')}</dt>
            <dd className="tabular">{th.phone}</dd>
            <dt>{t('sms.f_customer')}</dt>
            <dd>{th.customer?.name ?? <span className="muted">{t('sms.not_linked')}</span>}</dd>
            <dt>{t('sms.f_agreed')}</dt>
            <dd>{th.stopped ? <StatusBadge tone="warn" icon="x" label={t('sms.said_stop')} /> : th.agreed_at ? dateTime(th.agreed_at) : <span className="muted">{t('sms.no_agreement')}</span>}</dd>
            {th.channel === 'whatsapp' ? (
              <>
                <dt>{t('sms.f_window')}</dt>
                <dd>{th.window_open && th.window_until ? t('sms.window_until', { at: dateTime(th.window_until) }) : <span className="muted">{t('sms.window_closed')}</span>}</dd>
              </>
            ) : null}
          </dl>
          <p className="muted small">{th.channel === 'whatsapp' ? t('sms.side_wa') : t('sms.side_sms')}</p>
        </aside>
      </div>
      {reply ? (
        <Composer
          th={th}
          onSent={async () => {
            await load();
            await onChange();
          }}
        />
      ) : null}
      {linking ? (
        <CustomerPicker
          title={t('sms.link_title', { phone: th.phone })}
          onClose={() => setLinking(false)}
          onPick={async (c) => {
            await action('sms.link_customer', { id: th.id, customer: c.id });
            setLinking(false);
            toast(t('sms.linked', { name: c.name }));
            await load();
            await onChange();
          }}
        />
      ) : null}
    </div>
  );
}

function Composer({ th, onSent }: { th: Thread; onSent: () => Promise<void> }) {
  const { fail, toast } = useUi();
  const { data: setup } = useAction<Setup>('sms.setup');
  const [body, setBody] = useState('');
  const [tpl, setTpl] = useState('');
  const [busy, setBusy] = useState(false);
  const closed = th.channel === 'whatsapp' && !th.window_open;
  const templates = (setup?.templates ?? []).filter((x) => x.channel === th.channel);
  const chosen = templates.find((x) => x.id === tpl) ?? null;
  const ready = setup?.ready[th.channel] ?? true;
  if (th.stopped && !th.window_open && th.channel === 'whatsapp') {
    return (
      <p className="lc-note">
        <Icon name="bellOff" size={16} /> {t('sms.stopped_note')}
      </p>
    );
  }
  const send = async () => {
    setBusy(true);
    try {
      const r = await action<{ state: string; error: string | null }>('sms.reply', { thread: th.id, ...(chosen && th.channel === 'whatsapp' ? { template: chosen.id } : { body: body.trim() }) });
      if (r.state === 'failed') fail(new Error(r.error ?? t('sms.not_sent')));
      else toast(t('sms.sent_toast'));
      setBody('');
      setTpl('');
      await onSent();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const useTemplate = (id: string) => {
    setTpl(id);
    const x = templates.find((y) => y.id === id);
    if (x && th.channel === 'sms') {
      setBody(fill(x.body, { first_name: th.customer?.name.split(' ')[0] ?? null, name: th.customer?.name ?? null, business: setup?.business ?? '' }));
      setTpl('');
    }
  };
  const canSend = ready && !busy && (closed || (chosen && th.channel === 'whatsapp') ? !!chosen : body.trim().length > 0);
  return (
    <div className="lc-composer sms-composer">
      {!ready ? (
        <p className="sms-note warn">
          <Icon name="alert" size={14} /> {t(th.channel === 'whatsapp' ? 'sms.wa_not_ready' : 'sms.sms_not_ready')}
        </p>
      ) : closed ? (
        <p className="sms-note">
          <Icon name="lock" size={14} /> {t('sms.window_note')}
        </p>
      ) : th.stopped ? (
        <p className="sms-note warn">
          <Icon name="bellOff" size={14} /> {t('sms.stopped_reply_note')}
        </p>
      ) : null}
      {closed || (th.channel === 'whatsapp' && chosen) ? (
        <div className="sms-tpl-pick">
          <select className="input" value={tpl} aria-label={t('sms.choose_template')} onChange={(e) => setTpl(e.target.value)}>
            <option value="">{templates.length ? t('sms.choose_template') : t('sms.no_wa_templates')}</option>
            {templates.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
          {chosen ? (
            <p className="sms-tpl-preview">{templateText(chosen, { first_name: th.customer?.name.split(' ')[0] ?? null, name: th.customer?.name ?? null, business: setup?.business ?? '' })}</p>
          ) : null}
        </div>
      ) : (
        <textarea
          className="input textarea"
          rows={2}
          value={body}
          maxLength={1600}
          placeholder={t('sms.write_reply')}
          aria-label={t('sms.write_reply')}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSend) {
              e.preventDefault();
              void send();
            }
          }}
        />
      )}
      <div className="lc-composer-foot">
        <span className="sms-composer-left">
          {th.channel === 'sms' && body ? <LengthLine text={body} onPlain={() => setBody(plainLetters(body))} /> : null}
          {!closed && templates.length ? (
            <select className="input sms-mini-select" value="" aria-label={t('sms.use_template')} onChange={(e) => e.target.value && useTemplate(e.target.value)}>
              <option value="">{t('sms.use_template')}</option>
              {templates.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          ) : null}
          {th.channel === 'whatsapp' && chosen && !closed ? (
            <button type="button" className="link" onClick={() => setTpl('')}>
              {t('sms.write_instead')}
            </button>
          ) : null}
        </span>
        <span className="row-actions">
          <span className="muted lc-hint">{t('sms.ctrl_enter')}</span>
          <Button variant="primary" icon="send" busy={busy} disabled={!canSend} onClick={() => void send()}>
            {t('sms.send')}
          </Button>
        </span>
      </div>
    </div>
  );
}

/** A message to someone who has not written: a customer with a phone or any number. */
function NewMessage({ onClose, onSent }: { onClose: () => void; onSent: (thread: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: setup } = useAction<Setup>('sms.setup');
  const [channel, setChannel] = useState<Channel>('sms');
  const [customer, setCustomer] = useState<{ id: string; name: string; phone: string | null } | null>(null);
  const [phone, setPhone] = useState('');
  const [picking, setPicking] = useState(false);
  const [body, setBody] = useState('');
  const [tpl, setTpl] = useState('');
  const templates = (setup?.templates ?? []).filter((x) => x.channel === channel);
  const chosen = templates.find((x) => x.id === tpl) ?? null;
  const v = { first_name: customer?.name.split(' ')[0] ?? null, name: customer?.name ?? null, business: setup?.business ?? '' };
  const to = customer ? !!customer.phone : phone.replace(/\D/g, '').length >= 6;
  const ok = to && (channel === 'whatsapp' ? !!chosen : body.trim().length > 0) && (setup?.ready[channel] ?? false);
  return (
    <>
      <Dialog
        title={t('sms.new_message')}
        onClose={onClose}
        size="md"
        footer={
          <Button
            variant="primary"
            icon="send"
            busy={busy}
            disabled={!ok}
            onClick={() =>
              void run(async () => {
                const r = await action<{ thread: string; state: string; error: string | null }>('sms.start', {
                  channel,
                  ...(customer ? { customer: customer.id } : { phone: phone.trim() }),
                  ...(channel === 'whatsapp' ? { template: chosen?.id } : { body: body.trim() }),
                });
                if (r.state === 'failed') throw new Error(r.error ?? t('sms.not_sent'));
                await onSent(r.thread);
              }, t('sms.sent_toast'))
            }
          >
            {t('sms.send')}
          </Button>
        }
      >
        {!setup ? (
          <Skeleton h={240} />
        ) : (
          <div className="form-stack">
            <ChannelPick value={channel} onChange={(c) => (setChannel(c), setTpl(''))} ready={setup.ready} />
            {!setup.ready[channel] ? (
              <p className="callout callout-warn">
                <Icon name="alert" size={16} />
                <span>{t(channel === 'whatsapp' ? 'sms.wa_not_ready' : 'sms.sms_not_ready')}</span>
              </p>
            ) : null}
            <Field label={t('sms.to')} hint={customer ? undefined : t('sms.to_hint')}>
              {customer ? (
                <span className="sms-to">
                  <span className="vat-party">
                    <strong>{customer.name}</strong>
                    <span className="muted tabular">{customer.phone ?? t('sms.no_phone')}</span>
                  </span>
                  <IconButton icon="x" label={t('sms.remove')} onClick={() => setCustomer(null)} />
                </span>
              ) : (
                <span className="sms-to">
                  <input className="input" type="tel" inputMode="tel" value={phone} placeholder="+355 69 …" onChange={(e) => setPhone(e.target.value)} />
                  <Button icon="contact" onClick={() => setPicking(true)}>
                    {t('sms.pick_customer')}
                  </Button>
                </span>
              )}
            </Field>
            {channel === 'whatsapp' ? (
              <Field label={t('sms.template')} hint={t('sms.wa_start_hint')}>
                <select className="input" value={tpl} onChange={(e) => setTpl(e.target.value)}>
                  <option value="">{templates.length ? t('sms.choose_template') : t('sms.no_wa_templates')}</option>
                  {templates.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label={t('sms.message')}>
                <textarea className="input textarea" rows={4} maxLength={1600} value={body} onChange={(e) => setBody(e.target.value)} />
              </Field>
            )}
            {channel === 'sms' ? (
              <div className="sms-under">
                <LengthLine text={body} onPlain={() => setBody(plainLetters(body))} />
                {templates.length ? (
                  <select
                    className="input sms-mini-select"
                    value=""
                    aria-label={t('sms.use_template')}
                    onChange={(e) => {
                      const x = templates.find((y) => y.id === e.target.value);
                      if (x) setBody(fill(x.body, v));
                    }}
                  >
                    <option value="">{t('sms.use_template')}</option>
                    {templates.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            ) : chosen ? (
              <Bubble text={templateText(chosen, v)} channel="whatsapp" />
            ) : null}
            <p className="muted small">{t('sms.consent_reminder')}</p>
          </div>
        )}
      </Dialog>
      {picking ? (
        <CustomerPicker
          title={t('sms.pick_customer')}
          onClose={() => setPicking(false)}
          onPick={(c) => {
            setCustomer({ id: c.id, name: c.name, phone: c.phone });
            setPicking(false);
          }}
        />
      ) : null}
    </>
  );
}

function ChannelPick({ value, onChange, ready, disabled }: { value: Channel; onChange: (c: Channel) => void; ready: Record<Channel, boolean>; disabled?: boolean }) {
  return (
    <div className="option-list sms-channels" role="radiogroup" aria-label={t('sms.channel')}>
      {(['sms', 'whatsapp'] as const).map((c) => (
        <label key={c} className={`option-card${value === c ? ' selected' : ''}`}>
          <input type="radio" name="channel" checked={value === c} disabled={disabled} onChange={() => onChange(c)} />
          <span>
            <strong>
              <Icon name={CH_ICON[c]} size={16} /> {t(`sms.ch_${c}` as MessageKey)}
            </strong>
            <span className="muted small">{ready[c] ? t(`sms.ch_${c}_hint` as MessageKey) : t('sms.ch_off')}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

/** How the message looks on the customer's phone. */
function Bubble({ text, channel, from }: { text: string; channel: Channel; from?: string }) {
  return (
    <div className={`sms-phone sms-phone-${channel}`} aria-label={t('sms.preview')}>
      <div className="sms-phone-head">
        <span className="sms-phone-avatar" aria-hidden="true">
          <Icon name={CH_ICON[channel]} size={14} />
        </span>
        <strong>{from || t(`sms.ch_${channel}` as MessageKey)}</strong>
      </div>
      <div className="sms-phone-body">{text ? <p className="sms-bubble">{text}</p> : <p className="muted small sms-phone-empty">{t('sms.preview_empty')}</p>}</div>
    </div>
  );
}

// ---- campaigns ----------------------------------------------------------------------------------------

const C_FILTERS = ['all', 'draft', 'scheduled', 'sent'] as const;

function CampaignsTab({ params, set, onOpen, onNew }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onOpen: (id: string) => void; onNew?: () => void }) {
  const filter = (C_FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as (typeof C_FILTERS)[number]) : 'all';
  const { data } = useAction<CampaignRow[]>('sms.campaigns', { state: filter });
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle mkt-filters" role="radiogroup" aria-label={t('sms.show')}>
          {C_FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => set({ f: f === 'all' ? null : f })}>
              {t(`sms.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : !data.length && filter === 'all' ? (
        <EmptyState
          icon="send"
          title={t('sms.no_campaigns')}
          body={t('sms.no_campaigns_hint')}
          action={
            onNew ? (
              <Button icon="plus" onClick={onNew}>
                {t('sms.new_campaign')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Table
          view="sms.campaigns"
          head={[t('sms.c_campaign'), t('sms.audience'), t('sms.c_when'), t('sms.c_sent'), t('sms.c_delivered'), t('sms.c_replies'), t('sms.c_state')]}
          phoneHide={[1, 2, 3, 5]}
          left={[0, 1, 2]}
          empty={t('sms.none_found')}
          rows={data.map((c) => ({
            key: c.id,
            onClick: () => onOpen(c.id),
            cells: [
              <span className="vat-party" key="n">
                <span>{c.name}</span>
                <span className="muted">
                  {c.number} · {t(`sms.ch_${c.channel}` as MessageKey)}
                </span>
              </span>,
              c.audience ?? <span className="muted">—</span>,
              <span key="w" className="tabular">
                {c.started_at ? dateTime(c.started_at) : c.scheduled_at ? dateTime(c.scheduled_at) : <span className="muted">—</span>}
              </span>,
              <span key="s" className="tabular">
                {c.state === 'draft' || c.state === 'scheduled' ? '—' : c.sent}
              </span>,
              <span key="d" className="tabular">
                {c.sent ? pct(c.delivered, c.sent) : '—'}
              </span>,
              <span key="r" className="tabular">
                {c.sent ? c.replies : '—'}
              </span>,
              <CampaignBadge key="st" state={c.state} />,
            ],
          }))}
        />
      )}
    </section>
  );
}

const R_FILTERS = ['all', 'delivered', 'failed', 'replied', 'queued'] as const;

function CampaignDialog({ id, onClose, onChange, onEdit, onOpen }: { id: string; onClose: () => void; onChange: () => Promise<void>; onEdit: (id: string) => void; onOpen: (id: string) => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { data: c, reload } = useAction<Campaign>('sms.campaign', { id });
  const [ask, setAsk] = useState<null | 'send' | 'test' | 'delete'>(null);
  const done = async () => {
    setAsk(null);
    await reload();
    await onChange();
  };
  useEffect(() => {
    if (c?.state !== 'sending') return;
    const timer = setInterval(() => void reload(), 10_000);
    return () => clearInterval(timer);
  }, [c?.state, reload]);
  if (!c) {
    return (
      <Dialog title={t('sms.campaign')} onClose={onClose} size="xl">
        <Skeleton h={360} />
      </Dialog>
    );
  }
  const manage = can('sms.manage');
  const send = can('sms.send');
  const out = c.state === 'sent' || c.state === 'sending' || c.state === 'cancelled';
  return (
    <>
      <Dialog
        title={`${c.number} · ${c.name}`}
        onClose={onClose}
        size="xl"
        footer={
          <>
            {manage ? (
              <Button
                variant="ghost"
                icon="copy"
                busy={busy}
                onClick={() =>
                  void run(async () => {
                    const copy = await action<{ id: string }>('sms.duplicate_campaign', { id: c.id });
                    await onChange();
                    onOpen(copy.id);
                  }, t('sms.copied'))
                }
              >
                {t('sms.duplicate')}
              </Button>
            ) : null}
            {(c.state === 'draft' || (c.state === 'cancelled' && !c.sent)) && manage ? (
              <Button variant="ghost" icon="trash" onClick={() => setAsk('delete')}>
                {t('sms.delete')}
              </Button>
            ) : null}
            {c.state === 'draft' && manage ? (
              <>
                <Button variant="ghost" icon="send" onClick={() => setAsk('test')} disabled={!c.ready}>
                  {t('sms.send_test')}
                </Button>
                <Button icon="edit" onClick={() => onEdit(c.id)}>
                  {t('sms.edit')}
                </Button>
                {send ? (
                  <Button variant="primary" icon="send" onClick={() => setAsk('send')}>
                    {t('sms.send_campaign')}
                  </Button>
                ) : null}
              </>
            ) : null}
            {(c.state === 'scheduled' || c.state === 'sending') && send ? (
              <Button
                variant="danger"
                icon="x"
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await action('sms.cancel', { id: c.id });
                      await done();
                    },
                    c.state === 'scheduled' ? t('sms.back_to_draft') : t('sms.stopped'),
                  )
                }
              >
                {c.state === 'scheduled' ? t('sms.unschedule') : t('sms.stop_sending')}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('sms.audience')}</span>
              <strong>{c.audience_name ?? '—'}</strong>
            </div>
            <div>
              <span className="muted">{out ? t('sms.c_sent') : t('sms.will_reach')}</span>
              <strong className="tabular">{out ? `${c.sent} / ${c.recipients}` : (c.audience_count?.reachable ?? '—')}</strong>
            </div>
            <div>
              <span className="muted">{c.state === 'scheduled' ? t('sms.goes_out') : t('sms.c_when')}</span>
              <strong>{c.started_at ? dateTime(c.started_at) : c.scheduled_at ? dateTime(c.scheduled_at) : '—'}</strong>
            </div>
            <div>
              <span className="muted">{t('sms.channel')}</span>
              <ChannelTag channel={c.channel} />
            </div>
            <CampaignBadge state={c.state} />
          </div>
          {c.last_error && (c.state === 'sending' || c.state === 'scheduled') ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>{t('sms.waiting_twilio', { error: c.last_error })}</span>
            </p>
          ) : c.state === 'sending' ? (
            <p className="callout callout-info">
              <Icon name="send" size={16} />
              <span>{t('sms.sending_note', { n: Math.max(0, c.recipients - c.sent - c.failed) })}</span>
            </p>
          ) : c.state === 'draft' && !c.ready ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>{t(c.channel === 'whatsapp' ? 'sms.wa_not_ready' : 'sms.sms_not_ready')}</span>
            </p>
          ) : c.state === 'draft' && c.audience_count ? (
            <p className="callout callout-info">
              <Icon name="users" size={16} />
              <span>
                {t('sms.reach_note', { n: c.audience_count.reachable, total: c.audience_count.total })}
                {c.tested_at ? ` ${t('sms.tested_at', { at: dateTime(c.tested_at) })}` : ''}
              </span>
            </p>
          ) : null}
          <div className="sms-camp">
            <div className="form-stack">
              {out ? (
                <section className="rep-kpis mkt-result-kpis">
                  <Kpi label={t('sms.c_sent')} value={String(c.sent)} hint={c.failed ? tn('sms.n_failed', c.failed) : t('sms.none_failed')} warn={c.failed > 0} />
                  <Kpi label={t('sms.c_delivered')} value={pct(c.delivered, c.sent)} hint={tn('sms.n_people', c.delivered)} />
                  <Kpi label={t('sms.c_replies')} value={String(c.replies)} hint={pct(c.replies, c.sent)} />
                  <Kpi label={t('sms.c_opted_out')} value={String(c.opted_out)} hint={pct(c.opted_out, c.sent)} warn={c.sent > 0 && c.opted_out / c.sent > 0.02} />
                </section>
              ) : null}
              {out ? <Recipients c={c} /> : <p className="muted">{c.channel === 'sms' ? t('sms.draft_note_sms') : t('sms.draft_note_wa', { name: c.template_name ?? '—' })}</p>}
            </div>
            <div className="form-stack">
              <Bubble text={c.preview} channel={c.channel} />
              {c.channel === 'sms' && c.preview ? <LengthLine text={c.preview} /> : null}
              {c.sent_by_name ? <p className="muted small">{t('sms.sent_by', { name: c.sent_by_name })}</p> : null}
            </div>
          </div>
        </div>
      </Dialog>
      {ask === 'send' ? <SendDialog c={c} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'test' ? <TestDialog c={c} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'delete' ? (
        <Dialog
          title={t('sms.delete_title', { n: c.number })}
          onClose={() => setAsk(null)}
          size="sm"
          footer={
            <Button
              variant="danger"
              icon="trash"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('sms.delete_campaign', { id: c.id });
                  setAsk(null);
                  onClose();
                  await onChange();
                }, t('sms.deleted'))
              }
            >
              {t('sms.delete')}
            </Button>
          }
        >
          <p>{t('sms.delete_note', { name: c.name })}</p>
        </Dialog>
      ) : null}
    </>
  );
}

function Recipients({ c }: { c: Campaign }) {
  const [filter, setFilter] = useState<(typeof R_FILTERS)[number]>('all');
  const [offset, setOffset] = useState(0);
  const { data } = useAction<{
    total: number;
    rows: { id: string; phone: string; state: MState; error: string | null; customer_name: string | null; replied: boolean; delivered_at: string | null; sent_at: string | null }[];
  }>('sms.recipients', { campaign: c.id, filter, offset });
  return (
    <section>
      <div className="toolbar">
        <div className="segmented subtle mkt-filters" role="radiogroup" aria-label={t('sms.show')}>
          {R_FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => (setFilter(f), setOffset(0))}>
              {t(`sms.rf_${f}` as MessageKey)}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : (
        <>
          <Table
            head={[t('sms.c_person'), t('sms.c_when'), t('sms.c_state')]}
            phoneHide={[1]}
            left={[0]}
            empty={t('sms.nobody_here')}
            rows={data.rows.map((r) => {
              const tick = TICK[r.state];
              const bad = r.state === 'failed' || r.state === 'undelivered' || r.state === 'skipped';
              return {
                key: r.id,
                cells: [
                  <span className="vat-party" key="p">
                    <span>{r.customer_name ?? r.phone}</span>
                    <span className="muted tabular">{r.phone}</span>
                  </span>,
                  <span key="w" className="tabular">
                    {r.delivered_at ? dateTime(r.delivered_at) : r.sent_at ? dateTime(r.sent_at) : '—'}
                  </span>,
                  <span key="s" className="sms-rstate" title={r.error ?? undefined}>
                    {tick ? <StatusBadge tone={bad ? 'danger' : r.state === 'delivered' || r.state === 'read' ? 'ok' : 'neutral'} icon={tick.icon} label={t(tick.key)} /> : null}
                    {r.replied ? <StatusBadge tone="info" icon="reply" label={t('sms.replied')} /> : null}
                  </span>,
                ],
              };
            })}
          />
          {data.total > 100 ? (
            <div className="row-actions mkt-pager">
              <span className="muted small">{t('sms.showing', { from: data.total ? offset + 1 : 0, to: offset + data.rows.length, total: data.total })}</span>
              <IconButton icon="back" label={t('sms.prev')} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))} />
              <IconButton icon="chevronRight" label={t('sms.next')} disabled={offset + data.rows.length >= data.total} onClick={() => setOffset(offset + 100)} />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function SendDialog({ c, onClose, onDone }: { c: Campaign; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [at, setAt] = useState(() => {
    const d = new Date(Date.now() + 86_400_000);
    d.setHours(10, 0, 0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  });
  const reach = c.audience_count?.reachable ?? 0;
  const parts = c.channel === 'sms' ? Math.max(1, c.length.segments) : 1;
  return (
    <Dialog
      title={t('sms.send_title', { name: c.name })}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant="primary"
          icon={when === 'later' ? 'clock' : 'send'}
          busy={busy}
          disabled={!reach || !c.ready}
          onClick={() =>
            void run(
              async () => {
                await action('sms.schedule', { id: c.id, at: when === 'later' ? new Date(at).toISOString() : null });
                await onDone();
              },
              when === 'later' ? t('sms.scheduled_msg') : t('sms.sending_msg'),
            )
          }
        >
          {when === 'later' ? t('sms.schedule') : tn('sms.send_to', reach)}
        </Button>
      }
    >
      <div className="form-stack">
        <p>{t('sms.send_note', { n: reach, audience: c.audience_name ?? '' })}</p>
        {c.channel === 'sms' ? <p className="muted small">{t('sms.parts_note', { n: reach * parts, parts })}</p> : null}
        {!c.tested_at ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('sms.not_tested')}</span>
          </p>
        ) : null}
        <div className="option-list" role="radiogroup" aria-label={t('sms.when')}>
          {(['now', 'later'] as const).map((w) => (
            <label key={w} className={`option-card${when === w ? ' selected' : ''}`}>
              <input type="radio" name="when" checked={when === w} onChange={() => setWhen(w)} />
              <span>
                <strong>{t(`sms.when_${w}` as MessageKey)}</strong>
                <span className="muted small">{t(`sms.when_${w}_hint` as MessageKey)}</span>
              </span>
            </label>
          ))}
        </div>
        {when === 'later' ? (
          <Field label={t('sms.send_at')}>
            <input className="input" type="datetime-local" value={at} onChange={(e) => e.target.value && setAt(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
}

function TestDialog({ c, onClose, onDone }: { c: Campaign; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [to, setTo] = useState('');
  const ok = to.replace(/\D/g, '').length >= 6;
  return (
    <Dialog
      title={t('sms.test_title')}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant="primary"
          icon="send"
          busy={busy}
          disabled={!ok}
          onClick={() =>
            void run(async () => {
              await action('sms.test', { id: c.id, phone: to.trim() });
              await onDone();
            }, t('sms.test_sent'))
          }
        >
          {t('sms.send_test')}
        </Button>
      }
    >
      <Field label={t('sms.test_to')} hint={t('sms.test_hint')}>
        <input className="input" type="tel" inputMode="tel" value={to} placeholder="+355 69 …" onChange={(e) => setTo(e.target.value)} />
      </Field>
    </Dialog>
  );
}

// ---- the campaign editor --------------------------------------------------------------------------

function Editor({ id, onClose, onSaved }: { id: string | null; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: setup } = useAction<Setup>('sms.setup');
  const [ready, setReady] = useState(!id);
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<Channel>('sms');
  const [body, setBody] = useState('');
  const [template, setTemplate] = useState('');
  const [audience, setAudience] = useState('');
  const [addStop, setAddStop] = useState(true);
  const [tried, setTried] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!id) return;
    void action<Campaign>('sms.campaign', { id }).then((c) => {
      setName(c.name);
      setChannel(c.channel);
      setBody(c.body);
      setTemplate(c.template ?? '');
      setAudience(c.audience ?? '');
      setAddStop(c.add_stop);
      setReady(true);
    });
  }, [id]);
  useEffect(() => {
    const best = setup?.audiences.reduce<Setup['audiences'][number] | null>((b, x) => (!b || x[channel] > b[channel] ? x : b), null);
    if (!audience && best) setAudience(best.id);
  }, [setup]);
  const templates = (setup?.templates ?? []).filter((x) => x.channel === channel);
  const tpl = templates.find((x) => x.id === template) ?? null;
  const aud = setup?.audiences.find((a) => a.id === audience);
  const v = sample(setup?.business ?? '');
  const preview = channel === 'whatsapp' ? (tpl ? templateText(tpl, v) : '') : `${fill(body, v).trim()}${addStop && setup?.stop_note && body.trim() ? `\n${setup.stop_note}` : ''}`;
  const errors = {
    name: !name.trim() ? t('sms.e_name') : null,
    body: channel === 'sms' && !body.trim() ? t('sms.e_body') : null,
    template: channel === 'whatsapp' && !tpl ? t('sms.e_template') : null,
  };
  const insert = (k: Variable) => {
    const el = area.current;
    const token = k === 'first_name' ? `{first_name|${t('sms.fallback_there')}}` : `{${k}}`;
    if (!el) return setBody(body + token);
    const s = el.selectionStart ?? body.length;
    const e = el.selectionEnd ?? body.length;
    setBody(body.slice(0, s) + token + body.slice(e));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + token.length, s + token.length);
    });
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const c = await action<{ id: string }>('sms.save_campaign', {
        id,
        name: name.trim(),
        channel,
        body: channel === 'sms' ? body : '',
        template: channel === 'whatsapp' ? template || null : null,
        audience: audience || null,
        add_stop: addStop,
      });
      await onSaved(c.id);
    }, t('sms.saved'));
  };
  return (
    <Dialog
      title={id ? t('sms.edit_title') : t('sms.new_campaign')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="inv-foot-total">
            <span className="muted">{t('sms.will_reach')}</span>
            <strong>{aud ? tn('sms.n_people', aud[channel]) : '—'}</strong>
          </span>
          <span className="foot-actions">
            <Button variant="primary" icon="check" busy={busy} onClick={save}>
              {t('sms.save_draft')}
            </Button>
          </span>
        </>
      }
    >
      {!setup || !ready ? (
        <Skeleton h={420} />
      ) : (
        <div className="sms-editor">
          <div className="form-stack sub-form">
            <Field label={t('sms.name')} required error={tried ? errors.name : null} hint={t('sms.name_hint')}>
              <input className="input" value={name} maxLength={120} placeholder={t('sms.name_example')} onChange={(e) => setName(e.target.value)} />
            </Field>
            <ChannelPick value={channel} onChange={(c) => (setChannel(c), setTemplate(''))} ready={setup.ready} />
            <Field label={t('sms.audience')} hint={aud ? tn('sms.n_agreed', aud[channel]) : t('sms.audience_hint')}>
              <select className="input" value={audience} onChange={(e) => setAudience(e.target.value)}>
                <option value="">{t('sms.choose_later')}</option>
                {setup.audiences.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a[channel]})
                  </option>
                ))}
              </select>
            </Field>
            {channel === 'sms' ? (
              <>
                <Field label={t('sms.message')} required error={tried ? errors.body : null}>
                  <textarea ref={area} className="input textarea" rows={6} maxLength={1600} value={body} placeholder={t('sms.body_example')} onChange={(e) => setBody(e.target.value)} />
                </Field>
                <div className="sms-under">
                  <LengthLine text={preview} onPlain={() => setBody(plainLetters(body))} />
                  <span className="sms-vars" role="group" aria-label={t('sms.insert')}>
                    <span className="muted small">{t('sms.insert')}</span>
                    {VARIABLES.map((k) => (
                      <Button key={k} size="sm" variant="ghost" onClick={() => insert(k)}>
                        {t(`sms.var_${k}` as MessageKey)}
                      </Button>
                    ))}
                  </span>
                </div>
                {templates.length ? (
                  <Field label={t('sms.start_from')}>
                    <select
                      className="input"
                      value=""
                      onChange={(e) => {
                        const x = templates.find((y) => y.id === e.target.value);
                        if (x) setBody(x.body);
                      }}
                    >
                      <option value="">{t('sms.choose_template')}</option>
                      {templates.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                <label className="setting-row sms-row">
                  <span>
                    <strong>{t('sms.add_stop')}</strong>
                    <span className="muted small">{t('sms.add_stop_hint', { note: setup.stop_note })}</span>
                  </span>
                  <Switch checked={addStop} onChange={setAddStop} label={t('sms.add_stop')} />
                </label>
              </>
            ) : (
              <Field label={t('sms.template')} required error={tried ? errors.template : null} hint={t('sms.wa_campaign_hint')}>
                <select className="input" value={template} onChange={(e) => setTemplate(e.target.value)}>
                  <option value="">{templates.length ? t('sms.choose_template') : t('sms.no_wa_templates')}</option>
                  {templates.map((x) => (
                    <option key={x.id} value={x.id} disabled={!x.content_sid}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
          <div className="sms-editor-preview">
            <Bubble text={preview} channel={channel} from={setup.business} />
            <p className="muted small">{t('sms.preview_note')}</p>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ---- templates ------------------------------------------------------------------------------------

function TemplatesTab() {
  const { can } = useSession();
  const { data, reload } = useAction<Template[]>('sms.templates');
  const [edit, setEdit] = useState<Template | 'new' | null>(null);
  const manage = can('sms.manage');
  return (
    <section className="panel">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{t('sms.templates')}</h2>
          <p className="muted small">{t('sms.templates_hint')}</p>
        </div>
        {manage ? (
          <Button size="sm" icon="plus" onClick={() => setEdit('new')}>
            {t('sms.new_template')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : data.length ? (
        <ul className="sms-templates">
          {data.map((x) => (
            <li key={x.id} className={x.active ? '' : 'inactive'}>
              <div className="sms-template-head">
                <strong>{x.name}</strong>
                <ChannelTag channel={x.channel} />
              </div>
              <p className="sms-template-body">{x.body || <span className="muted">{x.content_sid}</span>}</p>
              <span className="muted small">
                {[t(`sms.purpose_${x.purpose}` as MessageKey), x.used ? tn('sms.n_used', x.used) : null, x.active ? null : t('sms.hidden')].filter(Boolean).join(' · ')}
              </span>
              {x.channel === 'whatsapp' && !x.content_sid ? (
                <span className="sms-note warn">
                  <Icon name="alert" size={14} /> {t('sms.no_sid')}
                </span>
              ) : null}
              {manage ? (
                <div className="row-actions">
                  <Button size="sm" variant="ghost" icon="edit" onClick={() => setEdit(x)}>
                    {t('sms.edit')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="file" title={t('sms.no_templates')} body={t('sms.no_templates_hint')} />
      )}
      {edit ? (
        <TemplateDialog
          x={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            setEdit(null);
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

function TemplateDialog({ x, onClose, onSaved }: { x: Template | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const [name, setName] = useState(x?.name ?? '');
  const [channel, setChannel] = useState<Channel>(x?.channel ?? 'sms');
  const [purpose, setPurpose] = useState<Template['purpose']>(x?.purpose ?? 'service');
  const [body, setBody] = useState(x?.body ?? '');
  const [sid, setSid] = useState(x?.content_sid ?? '');
  const [vars, setVars] = useState<Variable[]>(x?.variables ?? []);
  const [active, setActive] = useState(x?.active ?? true);
  const [tried, setTried] = useState(false);
  const [sure, setSure] = useState(false);
  const places = useMemo(() => {
    const n = [...body.matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]));
    return n.length ? Math.min(10, Math.max(...n)) : 0;
  }, [body]);
  useEffect(() => {
    if (channel !== 'whatsapp') return;
    setVars((v) => Array.from({ length: places }, (_, i) => v[i] ?? 'first_name'));
  }, [places, channel]);
  const errors = {
    name: !name.trim() ? t('sms.e_name') : null,
    body: channel === 'sms' && !body.trim() ? t('sms.e_body') : null,
    sid: channel === 'whatsapp' && !/^HX[0-9a-fA-F]{32}$/.test(sid.trim()) ? t('sms.e_sid') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('sms.save_template', {
        id: x?.id ?? null,
        name: name.trim(),
        channel,
        purpose,
        body,
        content_sid: channel === 'whatsapp' ? sid.trim() : null,
        variables: channel === 'whatsapp' ? vars : [],
        active,
      });
      await onSaved();
    }, t('sms.template_saved'));
  };
  return (
    <Dialog
      title={x ? t('sms.edit_template') : t('sms.new_template')}
      onClose={onClose}
      size="md"
      footer={
        <>
          {x ? (
            <Button
              variant="ghost"
              icon="trash"
              busy={busy}
              onClick={() => {
                if (!sure) return setSure(true);
                void run(async () => {
                  const r = await action<{ hidden: boolean }>('sms.delete_template', { id: x.id });
                  toast(r.hidden ? t('sms.template_hidden') : t('sms.template_deleted'));
                  await onSaved();
                });
              }}
            >
              {sure ? t('sms.sure_delete') : t('sms.delete')}
            </Button>
          ) : null}
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('sms.name')} required error={tried ? errors.name : null}>
            <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('sms.purpose')} hint={t(`sms.purpose_${purpose}_hint` as MessageKey)}>
            <select className="input" value={purpose} onChange={(e) => setPurpose(e.target.value as Template['purpose'])}>
              <option value="service">{t('sms.purpose_service')}</option>
              <option value="marketing">{t('sms.purpose_marketing')}</option>
            </select>
          </Field>
        </div>
        <ChannelPick value={channel} onChange={setChannel} ready={{ sms: true, whatsapp: true }} disabled={!!x?.used} />
        {channel === 'whatsapp' ? (
          <p className="callout callout-info">
            <Icon name="info" size={16} />
            <span>{t('sms.wa_template_note')}</span>
          </p>
        ) : null}
        <Field
          label={channel === 'whatsapp' ? t('sms.wa_text') : t('sms.message')}
          required={channel === 'sms'}
          error={tried ? errors.body : null}
          hint={channel === 'whatsapp' ? t('sms.wa_text_hint') : t('sms.sms_text_hint')}
        >
          <textarea className="input textarea" rows={4} maxLength={1600} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        {channel === 'sms' ? <LengthLine text={fill(body, sample('Demo Bistro'))} onPlain={() => setBody(plainLetters(body))} /> : null}
        {channel === 'whatsapp' ? (
          <>
            <Field label={t('sms.content_sid')} required error={tried ? errors.sid : null} hint={t('sms.content_sid_hint')}>
              <input className="input tabular" value={sid} spellCheck={false} autoComplete="off" placeholder="HX…" onChange={(e) => setSid(e.target.value.trim())} />
            </Field>
            {vars.length ? (
              <fieldset className="option-group">
                <legend>{t('sms.fills')}</legend>
                <div className="sms-fills">
                  {vars.map((k, i) => (
                    <label key={i}>
                      <code>{`{{${i + 1}}}`}</code>
                      <select className="input" value={k} onChange={(e) => setVars(vars.map((y, j) => (j === i ? (e.target.value as Variable) : y)))}>
                        {VARIABLES.map((v) => (
                          <option key={v} value={v}>
                            {t(`sms.var_${v}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}
          </>
        ) : null}
        {x ? (
          <label className="setting-row sms-row">
            <span>
              <strong>{t('sms.active')}</strong>
              <span className="muted small">{t('sms.active_hint')}</span>
            </span>
            <Switch checked={active} onChange={setActive} label={t('sms.active')} />
          </label>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- who agreed -----------------------------------------------------------------------------------

const K_FILTERS = ['agreed', 'not_agreed', 'opted_out', 'no_phone', 'all'] as const;

function ContactsTab({ params, set, onChange }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const filter = (K_FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as (typeof K_FILTERS)[number]) : 'agreed';
  const channel: Channel = params.get('ch') === 'whatsapp' ? 'whatsapp' : 'sms';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [consent, setConsent] = useState<Contact | null>(null);
  useEffect(() => {
    const id = setTimeout(() => (setQ(search.trim()), setOffset(0)), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data, reload } = useAction<{ total: number; rows: Contact[] }>('sms.contacts', { channel, filter, offset, ...(q ? { search: q } : {}) });
  const manage = can('sms.manage');
  return (
    <section className="panel">
      <div className="toolbar sms-contacts-tools">
        <div className="segmented subtle" role="radiogroup" aria-label={t('sms.channel')}>
          {(['sms', 'whatsapp'] as const).map((c) => (
            <button key={c} role="radio" aria-checked={channel === c} className={channel === c ? 'active' : ''} onClick={() => (set({ ch: c === 'sms' ? null : c }), setOffset(0))}>
              <Icon name={CH_ICON[c]} size={14} />
              {t(`sms.ch_${c}` as MessageKey)}
            </button>
          ))}
        </div>
        <div className="segmented subtle mkt-filters" role="radiogroup" aria-label={t('sms.show')}>
          {K_FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => (set({ f: f === 'agreed' ? null : f }), setOffset(0))}>
              {t(`sms.kf_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('sms.search_customers')} aria-label={t('sms.search_customers')} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      <p className="muted small sms-consent-note">{t('sms.consent_note')}</p>
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <>
          <Table
            head={[t('sms.c_person'), t('sms.c_agreement'), t('sms.c_received'), '']}
            phoneHide={[2, 3]}
            left={[0, 1]}
            empty={t('sms.nobody_here')}
            rows={data.rows.map((p) => ({
              key: p.id,
              ...(manage && p.phone ? { onClick: () => setConsent(p) } : {}),
              cells: [
                <span className="vat-party" key="p">
                  <span>{p.name}</span>
                  <span className="muted tabular">{p.phone ?? t('sms.no_phone')}</span>
                </span>,
                p.reachable ? (
                  <span key="a" className="vat-party">
                    <StatusBadge tone="ok" icon="checkCircle" label={t('sms.agreed')} />
                    <span className="muted small">{p.agreed_at ? dateTime(p.agreed_at) : ''}</span>
                  </span>
                ) : p.opted_out_at && (!p.agreed_at || p.opted_out_at >= p.agreed_at) ? (
                  <span key="a" className="vat-party">
                    <StatusBadge tone="warn" icon="bellOff" label={t('sms.said_stop')} />
                    <span className="muted small">{dateTime(p.opted_out_at)}</span>
                  </span>
                ) : (
                  <StatusBadge key="a" tone="neutral" icon="minus" label={p.phone ? t('sms.not_agreed') : t('sms.no_phone')} />
                ),
                <span key="r" className="tabular">
                  {p.received || '—'}
                </span>,
                manage && p.phone ? (
                  <Button key="b" size="sm" variant="ghost" icon={p.reachable ? 'x' : 'check'} onClick={() => setConsent(p)}>
                    {p.reachable ? t('sms.withdraw') : t('sms.record_agreement')}
                  </Button>
                ) : null,
              ],
            }))}
          />
          <div className="row-actions mkt-pager">
            <span className="muted small">{t('sms.showing', { from: data.total ? offset + 1 : 0, to: offset + data.rows.length, total: data.total })}</span>
            <IconButton icon="back" label={t('sms.prev')} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))} />
            <IconButton icon="chevronRight" label={t('sms.next')} disabled={offset + data.rows.length >= data.total} onClick={() => setOffset(offset + 100)} />
          </div>
        </>
      )}
      {consent ? (
        <ConsentDialog
          p={consent}
          channel={channel}
          onClose={() => setConsent(null)}
          onDone={async () => {
            setConsent(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

function ConsentDialog({ p, channel, onClose, onDone }: { p: Contact; channel: Channel; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const agree = !p.reachable;
  const [sure, setSure] = useState(false);
  const [note, setNote] = useState('');
  const ch = t(`sms.ch_${channel}` as MessageKey);
  return (
    <Dialog
      title={agree ? t('sms.agree_title', { name: p.name, channel: ch }) : t('sms.withdraw_title', { name: p.name, channel: ch })}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant={agree ? 'primary' : 'danger'}
          icon={agree ? 'check' : 'x'}
          busy={busy}
          disabled={agree && !sure}
          onClick={() =>
            void run(
              async () => {
                await action('sms.set_consent', { customer: p.id, channel, agreed: agree, note: note.trim() || null });
                await onDone();
              },
              agree ? t('sms.agreement_saved') : t('sms.withdrawn'),
            )
          }
        >
          {agree ? t('sms.record_agreement') : t('sms.withdraw')}
        </Button>
      }
    >
      <div className="form-stack">
        {agree && p.opted_out_at ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('sms.was_out', { at: dateTime(p.opted_out_at) })}</span>
          </p>
        ) : null}
        {!agree ? <p>{t('sms.withdraw_note')}</p> : null}
        <p className="muted small tabular">{p.phone}</p>
        <Field label={t('sms.note')} hint={agree ? t('sms.note_agree_hint') : t('sms.optional')}>
          <input className="input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {agree ? (
          <label className="check-row">
            <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} />
            <span>{t('sms.agree_confirm', { channel: ch })}</span>
          </label>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- settings -------------------------------------------------------------------------------------

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

function SettingsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast, fail } = useUi();
  const { data, reload } = useAction<Settings>('sms.settings');
  const [f, setF] = useState<Settings | null>(null);
  const [token, setToken] = useState('');
  const [checking, setChecking] = useState(false);
  const [tried, setTried] = useState(false);
  const loaded = useRef(false);
  useEffect(() => {
    if (!data || loaded.current) return;
    loaded.current = true;
    setF({ ...data, site_url: data.site_url ?? (window.location.protocol === 'https:' ? window.location.origin : null) });
  }, [data]);
  if (!f || !data) return <Skeleton h={400} />;
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setF({ ...f, [k]: v });
  const errors = {
    account_sid: f.account_sid && !/^AC[0-9a-fA-F]{32}$/.test(f.account_sid) ? t('sms.e_account') : null,
    token: token && !/^[0-9a-fA-F]{32}$/.test(token) ? t('sms.e_token') : null,
    service_sid: f.service_sid && !/^MG[0-9a-fA-F]{32}$/.test(f.service_sid) ? t('sms.e_service') : null,
    site_url: f.site_url && !/^https:\/\/[a-z0-9.-]+(:\d+)?(\/[\w./-]*)?$/i.test(f.site_url) ? t('sms.e_site') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const out = await action<Settings>('sms.save_settings', {
        account_sid: f.account_sid || null,
        ...(token ? { token } : {}),
        sms_from: f.sms_from || null,
        service_sid: f.service_sid || null,
        whatsapp_from: f.whatsapp_from || null,
        site_url: f.site_url || null,
        business_name: f.business_name?.trim() || null,
        stop_note: f.stop_note,
        per_minute: f.per_minute,
        per_day: f.per_day,
        quiet_from: f.quiet_from,
        quiet_to: f.quiet_to,
      });
      setToken('');
      setF(out);
      await reload();
      await onChange();
    }, t('sms.settings_saved'));
  };
  const verify = async () => {
    setChecking(true);
    try {
      const r = await action<{ ok: boolean; name?: string; error?: string }>('sms.verify', {});
      if (r.ok) toast(t('sms.verified_toast', { name: r.name ?? '' }));
      else fail(new Error(r.error ?? ''));
      await reload();
      await onChange();
    } catch (err) {
      fail(err);
    } finally {
      setChecking(false);
    }
  };
  const err = (e: string | null) => (tried ? e : null);
  const copy = (text: string) => void navigator.clipboard?.writeText(text).then(() => toast(t('sms.copied_link')), fail);
  const missingNote = (ch: Channel) => data.missing[ch].map((m) => t(`sms.miss_${m}` as MessageKey)).join(', ');
  return (
    <div className="form-stack">
      {data.verified_at ? (
        <p className="callout callout-info">
          <Icon name="checkCircle" size={16} />
          <span>{t('sms.verified', { name: data.account_name ?? '', at: dateTime(data.verified_at) })}</span>
        </p>
      ) : data.last_error ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('sms.twilio_error', { error: data.last_error })}</span>
        </p>
      ) : null}
      {!data.can_keep_token ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('sms.no_key')}</span>
        </p>
      ) : data.has_token && !data.token_readable ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('sms.token_lost')}</span>
        </p>
      ) : null}
      <div className="sms-ready">
        {(['sms', 'whatsapp'] as const).map((ch) => (
          <div key={ch} className={`sms-ready-card${data.missing[ch].length ? '' : ' ok'}`}>
            <Icon name={data.missing[ch].length ? 'circle' : 'checkCircle'} size={18} />
            <span className="vat-party">
              <strong>{t(`sms.ch_${ch}` as MessageKey)}</strong>
              <span className="muted small">{data.missing[ch].length ? t('sms.missing', { what: missingNote(ch) }) : t('sms.ready')}</span>
            </span>
          </div>
        ))}
      </div>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('sms.account')}</h2>
          <p className="muted small">{t('sms.account_hint')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('sms.account_sid')} error={err(errors.account_sid)} hint={t('sms.account_sid_hint')}>
            <input className="input tabular" value={f.account_sid ?? ''} spellCheck={false} autoComplete="off" placeholder="AC…" onChange={(e) => set('account_sid', e.target.value.trim() || null)} />
          </Field>
          <Field label={t('sms.token')} error={err(errors.token)} hint={data.has_token && !token ? t('sms.token_kept') : t('sms.token_hint')}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={token}
              placeholder={data.has_token ? '••••••••' : ''}
              disabled={!data.can_keep_token}
              onChange={(e) => setToken(e.target.value.trim())}
            />
          </Field>
        </div>
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('sms.senders')}</h2>
          <p className="muted small">{t('sms.senders_hint')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('sms.sms_from')} hint={t('sms.sms_from_hint')}>
            <input className="input" value={f.sms_from ?? ''} maxLength={40} spellCheck={false} placeholder="+355… / MyBistro" onChange={(e) => set('sms_from', e.target.value || null)} />
          </Field>
          <Field label={t('sms.service_sid')} error={err(errors.service_sid)} hint={t('sms.service_sid_hint')}>
            <input className="input tabular" value={f.service_sid ?? ''} spellCheck={false} autoComplete="off" placeholder="MG…" onChange={(e) => set('service_sid', e.target.value.trim() || null)} />
          </Field>
          <Field label={t('sms.whatsapp_from')} hint={t('sms.whatsapp_from_hint')}>
            <input className="input" type="tel" value={f.whatsapp_from ?? ''} maxLength={40} placeholder="+355…" onChange={(e) => set('whatsapp_from', e.target.value || null)} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('sms.business_name')} hint={t('sms.business_name_hint')}>
            <input className="input" value={f.business_name ?? ''} maxLength={40} onChange={(e) => set('business_name', e.target.value || null)} />
          </Field>
          <Field label={t('sms.stop_note')} hint={t('sms.stop_note_hint')}>
            <input className="input" value={f.stop_note} maxLength={60} onChange={(e) => set('stop_note', e.target.value)} />
          </Field>
        </div>
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('sms.replies')}</h2>
          <p className="muted small">{t('sms.replies_hint')}</p>
        </div>
        <Field label={t('sms.site_url')} error={err(errors.site_url)} hint={t('sms.site_url_hint')}>
          <input className="input" value={f.site_url ?? ''} spellCheck={false} placeholder="https://" onChange={(e) => set('site_url', e.target.value.trim().replace(/\/+$/, '') || null)} />
        </Field>
        {data.inbound_url ? (
          <div className="lc-link-box">
            <p className="muted small">{t('sms.inbound_hint')}</p>
            <div className="lc-link">
              <code>{data.inbound_url}</code>
              <IconButton icon="copy" label={t('sms.copy')} onClick={() => copy(data.inbound_url!)} />
            </div>
          </div>
        ) : (
          <p className="muted small">{t('sms.inbound_after_save')}</p>
        )}
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('sms.pace')}</h2>
          <p className="muted small">{t('sms.pace_hint')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('sms.per_minute')}>
            <span className="input-unit">
              <input className="input" inputMode="numeric" value={f.per_minute} onChange={(e) => set('per_minute', Math.max(1, Math.min(600, Number(e.target.value.replace(/\D/g, '')) || 1)))} />
              <span>{t('sms.messages')}</span>
            </span>
          </Field>
          <Field label={t('sms.per_day')} hint={t('sms.per_day_hint')}>
            <span className="input-unit">
              <input className="input" inputMode="numeric" value={f.per_day} onChange={(e) => set('per_day', Math.max(1, Math.min(100000, Number(e.target.value.replace(/\D/g, '')) || 1)))} />
              <span>{t('sms.messages')}</span>
            </span>
          </Field>
        </div>
        <label className="setting-row sms-row">
          <span>
            <strong>{t('sms.quiet')}</strong>
            <span className="muted small">{t('sms.quiet_hint')}</span>
          </span>
          <Switch checked={f.quiet_from !== null && f.quiet_to !== null} onChange={(on) => setF({ ...f, quiet_from: on ? 21 : null, quiet_to: on ? 9 : null })} label={t('sms.quiet')} />
        </label>
        {f.quiet_from !== null && f.quiet_to !== null ? (
          <div className="form-grid">
            <Field label={t('sms.quiet_from')}>
              <select className="input" value={f.quiet_from} onChange={(e) => set('quiet_from', Number(e.target.value))}>
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {hh(h)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('sms.quiet_to')}>
              <select className="input" value={f.quiet_to} onChange={(e) => set('quiet_to', Number(e.target.value))}>
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {hh(h)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        ) : null}
      </section>
      <div className="row-actions mkt-settings-foot">
        <Button icon="shieldCheck" busy={checking} disabled={!data.account_sid || !data.has_token || !data.token_readable} onClick={() => void verify()}>
          {t('sms.verify')}
        </Button>
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
